import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  Search, X, SlidersHorizontal, MapPin, Calendar, 
  DollarSign, Star, Users, Hotel 
} from "lucide-react";
import { useCountries } from "@/hooks/useCountries";
import { useCities } from "@/hooks/useCities";

export interface AdvancedFilters {
  search: string;
  countryId: string;
  cityId: string;
  priceMin: number;
  priceMax: number;
  duration: string;
  rating: number;
  status: string;
  type: string;
  paymentStatus: string;
  services: string[];
}

interface PackageFiltersAdvancedProps {
  filters: AdvancedFilters;
  onFiltersChange: (filters: AdvancedFilters) => void;
  onReset: () => void;
  totalResults?: number;
}

export const PackageFiltersAdvanced = ({
  filters,
  onFiltersChange,
  onReset,
  totalResults
}: PackageFiltersAdvancedProps) => {
  const { data: countries = [] } = useCountries();
  const { data: allCities = [] } = useCities();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const cities = allCities.filter(city => 
    !filters.countryId || city.country_id === filters.countryId
  );

  const updateFilter = (key: keyof AdvancedFilters, value: any) => {
    onFiltersChange({ ...filters, [key]: value });
  };

  const toggleService = (service: string) => {
    const services = filters.services.includes(service)
      ? filters.services.filter(s => s !== service)
      : [...filters.services, service];
    updateFilter('services', services);
  };

  const availableServices = [
    'الفنادق',
    'الطيران الدولي',
    'الطيران الداخلي',
    'المواصلات',
    'الجولات السياحية',
    'الخدمات الإضافية'
  ];

  const hasActiveFilters = 
    filters.search ||
    filters.countryId ||
    filters.cityId ||
    filters.duration ||
    filters.rating > 0 ||
    filters.status !== 'all' ||
    filters.type !== 'all' ||
    filters.paymentStatus !== 'all' ||
    filters.services.length > 0 ||
    filters.priceMin > 0 ||
    filters.priceMax < 100000;

  return (
    <Card className="shadow-lg">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              البحث والفلترة
            </CardTitle>
            {totalResults !== undefined && (
              <p className="text-sm text-muted-foreground mt-1">
                عدد النتائج: {totalResults}
              </p>
            )}
          </div>
          <div className="flex gap-2">
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={onReset}
              >
                <X className="w-4 h-4 ml-1" />
                إعادة تعيين
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
            >
              <SlidersHorizontal className="w-4 h-4 ml-1" />
              {showAdvanced ? 'إخفاء' : 'خيارات متقدمة'}
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Basic Search */}
        <div className="relative">
          <Search className="absolute right-3 top-3 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="ابحث عن باقة..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pr-10"
          />
        </div>

        {/* Quick Filters Row */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Country */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm">
              <MapPin className="w-3 h-3" />
              الدولة
            </Label>
            <Select
              value={filters.countryId}
              onValueChange={(value) => {
                updateFilter('countryId', value);
                updateFilter('cityId', ''); // Reset city when country changes
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="جميع الدول" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الدول</SelectItem>
                {countries.map((country) => (
                  <SelectItem key={country.id} value={country.id}>
                    {country.flag_emoji} {country.name_ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* City */}
          <div className="space-y-2">
            <Label className="flex items-center gap-1 text-sm">
              <MapPin className="w-3 h-3" />
              المدينة
            </Label>
            <Select
              value={filters.cityId}
              onValueChange={(value) => updateFilter('cityId', value)}
              disabled={!filters.countryId || filters.countryId === 'all'}
            >
              <SelectTrigger>
                <SelectValue placeholder="جميع المدن" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع المدن</SelectItem>
                {cities.map((city) => (
                  <SelectItem key={city.id} value={city.id}>
                    {city.name_ar}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label className="text-sm">الحالة</Label>
            <Select
              value={filters.status}
              onValueChange={(value) => updateFilter('status', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="جميع الحالات" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الحالات</SelectItem>
                <SelectItem value="active">نشط</SelectItem>
                <SelectItem value="draft">مسودة</SelectItem>
                <SelectItem value="expired">منتهي</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label className="text-sm">النوع</Label>
            <Select
              value={filters.type}
              onValueChange={(value) => updateFilter('type', value)}
            >
              <SelectTrigger>
                <SelectValue placeholder="جميع الأنواع" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">جميع الأنواع</SelectItem>
                <SelectItem value="employee">أفراد</SelectItem>
                <SelectItem value="b2b">شركات (B2B)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="space-y-4 pt-4 border-t">
            {/* Price Range */}
            <div className="space-y-3">
              <Label className="flex items-center gap-1 text-sm font-semibold">
                <DollarSign className="w-4 h-4" />
                نطاق السعر
              </Label>
              <div className="px-2">
                <Slider
                  min={0}
                  max={100000}
                  step={1000}
                  value={[filters.priceMin, filters.priceMax]}
                  onValueChange={([min, max]) => {
                    updateFilter('priceMin', min);
                    updateFilter('priceMax', max);
                  }}
                  className="mb-2"
                />
                <div className="flex justify-between text-sm text-muted-foreground">
                  <span>{filters.priceMin.toLocaleString()} ريال</span>
                  <span>{filters.priceMax.toLocaleString()} ريال</span>
                </div>
              </div>
            </div>

            {/* Duration */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm font-semibold">
                <Calendar className="w-4 h-4" />
                المدة
              </Label>
              <Select
                value={filters.duration}
                onValueChange={(value) => updateFilter('duration', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="جميع المدد" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع المدد</SelectItem>
                  <SelectItem value="short">قصيرة (1-3 أيام)</SelectItem>
                  <SelectItem value="medium">متوسطة (4-7 أيام)</SelectItem>
                  <SelectItem value="long">طويلة (8+ أيام)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Rating */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm font-semibold">
                <Star className="w-4 h-4" />
                التقييم
              </Label>
              <Select
                value={filters.rating.toString()}
                onValueChange={(value) => updateFilter('rating', parseInt(value))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="جميع التقييمات" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">جميع التقييمات</SelectItem>
                  <SelectItem value="5">⭐⭐⭐⭐⭐ (5 نجوم)</SelectItem>
                  <SelectItem value="4">⭐⭐⭐⭐ (4+ نجوم)</SelectItem>
                  <SelectItem value="3">⭐⭐⭐ (3+ نجوم)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Payment Status */}
            <div className="space-y-2">
              <Label className="text-sm font-semibold">حالة الدفع</Label>
              <Select
                value={filters.paymentStatus}
                onValueChange={(value) => updateFilter('paymentStatus', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="جميع الحالات" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">جميع الحالات</SelectItem>
                  <SelectItem value="paid">مدفوع</SelectItem>
                  <SelectItem value="pending">قيد الانتظار</SelectItem>
                  <SelectItem value="unpaid">غير مدفوع</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Services Included */}
            <div className="space-y-2">
              <Label className="flex items-center gap-1 text-sm font-semibold">
                <Hotel className="w-4 h-4" />
                الخدمات المشمولة
              </Label>
              <div className="grid grid-cols-2 gap-2">
                {availableServices.map((service) => (
                  <div key={service} className="flex items-center space-x-2 space-x-reverse">
                    <Checkbox
                      id={service}
                      checked={filters.services.includes(service)}
                      onCheckedChange={() => toggleService(service)}
                    />
                    <label
                      htmlFor={service}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                    >
                      {service}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Active Filters Display */}
        {hasActiveFilters && (
          <div className="flex flex-wrap gap-2 pt-2">
            {filters.search && (
              <Badge variant="secondary">
                بحث: {filters.search}
                <X 
                  className="w-3 h-3 mr-1 cursor-pointer" 
                  onClick={() => updateFilter('search', '')}
                />
              </Badge>
            )}
            {filters.countryId && filters.countryId !== 'all' && (
              <Badge variant="secondary">
                {countries.find(c => c.id === filters.countryId)?.name_ar}
                <X 
                  className="w-3 h-3 mr-1 cursor-pointer" 
                  onClick={() => updateFilter('countryId', 'all')}
                />
              </Badge>
            )}
            {filters.cityId && filters.cityId !== 'all' && (
              <Badge variant="secondary">
                {cities.find(c => c.id === filters.cityId)?.name_ar}
                <X 
                  className="w-3 h-3 mr-1 cursor-pointer" 
                  onClick={() => updateFilter('cityId', 'all')}
                />
              </Badge>
            )}
            {filters.services.map((service) => (
              <Badge key={service} variant="secondary">
                {service}
                <X 
                  className="w-3 h-3 mr-1 cursor-pointer" 
                  onClick={() => toggleService(service)}
                />
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
